import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { TimeReportingFormTypes } from "models/formTypes";
import { SalaryDeviationConstants } from "utils/constants";
import { CompFormContainerProps } from "models/propTypes/timeReportingPropTypes";
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Skeletons, StyledButton } from "components/common";
import { useCreateAllowanceDialog } from "components/salaryDeviation";
import TimeReportHelperInstance from "components/timeReporting/timeReportHelper";
import { useTimeReportingService, useSalaryDeviationsService } from "serviceHooks";
import MonthSummary from "../common/monthSummary";
//#endregion
import { SalaryDeviationsService } from "services";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

const CompFormContainer: ForwardRefRenderFunction<FormHandles, CompFormContainerProps> = (props, ref) => {
  const salaryDeviationsService = useSalaryDeviationsService();
  const timeReportingService = useTimeReportingService();
  const { employeeId, startDate } = props;

  const [loading, setLoading] = React.useState<boolean>(true);
  const [monthWeeks, setMonthWeeks] = React.useState<TimeReportingFormTypes.MonthWeek[]>([]);
  const [timeReportRows, setTimeReportRows] = React.useState<TimeReportingFormTypes.MonthlyTimeReportRow[]>([]);
  const [allowanceSummary, setAllowanceSummary] = React.useState<string[]>([]);
  const [compHours, setCompHours] = React.useState<number>();
  const [employeeName, setEmployeeName] = React.useState<string>();

  const [isSalaryAdmin, loggedInUserId] = useSelector((state: AppState) => [
    state.auth.selectedCompany.loggedInUserIsSalaryAdmin,
    state.auth.loggedInUser.personId,
  ]);

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const weekStartDate = startDate && new Date(startDate);
  const yearAndMonth = weekStartDate && DateHelper.getYearMonth(weekStartDate);

  React.useEffect(() => {
    loadTimeReportMonthSummary();
  }, []);

  const loadTimeReportMonthSummary = () => {
    loadAllowancesSummary();
    loadSuggestedCompensationTime();
    loadTimeReportsSummary();
  };

  const loadTimeReportsSummary = () => {
    const monthStartDate = weekStartDate && DateHelper.getFirstDayOfMonth(weekStartDate);
    const monthEndDate = weekStartDate && DateHelper.getLastDayOfMonth(weekStartDate);
    monthStartDate &&
      monthEndDate &&
      timeReportingService
        .filter(employeeId, monthStartDate, monthEndDate)
        .then((res) => {
          if (res.weeks) {
            const monthRows: TimeReportingFormTypes.MonthlyTimeReportRow[] = [];
            const weeks: TimeReportingFormTypes.MonthWeek[] = [];
            res.weeks.forEach((week, index) => {
              if (week.weekSegments) {
                if (week.weekSegments.length > 1) {
                  const segment = index === 0 ? week.weekSegments[1] : week.weekSegments[0];
                  calculateWeekHours(segment, week, monthRows, weeks);
                } else {
                  calculateWeekHours(week.weekSegments[0], week, monthRows, weeks);
                }
              }
            });

            setMonthWeeks(weeks);
            setTimeReportRows(monthRows);
            setEmployeeName(res.employeeName);
          }
        })
        .finally(() => {
          setLoading(false);
        });
  };

  const loadAllowancesSummary = () => {
    yearAndMonth &&
      salaryDeviationsService
        .filter({
          personId: employeeId,
          yearAndMonth: yearAndMonth,
          types: SalaryDeviationConstants.AllowanceTypes,
          includePersonsWithNoDeviations: false,
          page: 0,
          orderByDescending: false,
          thenByDescending: false,
          isAutoPaginating: false,
        })
        .then((res) => {
          if (res.data.items) {
            const allowanceItems: string[] = [];
            res.data.items
              .filter((deviation) => deviation.personId === employeeId)
              .forEach((deviation) => {
                allowanceItems.push(
                  `${SalaryDeviationsService.getSalaryDeviationTypeText(
                    deviation.deviationType,
                    deviation.customDeviationName
                  )}: (${salaryDeviationsService.getSalaryDeviationDetailsText(deviation.summary)})`
                );
              });

            setAllowanceSummary(allowanceItems);
          }
        });
  };

  const loadSuggestedCompensationTime = () => {
    weekStartDate &&
      timeReportingService
        .getCompTime(employeeId, weekStartDate?.getFullYear(), weekStartDate.getMonth())
        .then((res) => {
          setCompHours(res.suggestedCompensationTime + res.totalExistingCompTime);
        });
  };

  const calculateWeekHours = (
    segment: WintSuperkollAPIModelTimeReporting.WeekSegment,
    week: WintSuperkollAPIModelTimeReporting.Week,
    monthRows: TimeReportingFormTypes.MonthlyTimeReportRow[],
    weeks: TimeReportingFormTypes.MonthWeek[]
  ) => {
    const start = new Date(segment.weekSegmentStart);
    const end = new Date(segment.weekSegmentEnd);
    const weekName = `${start.getDate()} - ${end.getDate()}`;

    weeks.push({ weekNumber: week.weekNumber, displayText: weekName } as TimeReportingFormTypes.MonthWeek);

    const filteredTimeReports = week.timeReports?.filter(
      (day) => new Date(day.timeLogDate) >= start && new Date(day.timeLogDate) <= end
    );
    filteredTimeReports?.forEach((day) => {
      TimeReportHelperInstance.calculteProjectHours(weekName, monthRows, day.projectTimes);
      TimeReportHelperInstance.calculateDeviationHours(weekName, monthRows, day.deviations);
    });
  };

  const createAllowanceDialog = useCreateAllowanceDialog(loadAllowancesSummary);

  const onCreateAllowanceClicked = () => {
    dispatch(
      displayDialog(createAllowanceDialog({ yearMonth: yearAndMonth, personId: employeeId, personName: employeeName }))
    );
  };

  return loading ? (
    <Skeletons.FormSkeleton></Skeletons.FormSkeleton>
  ) : (
    <>
      {monthWeeks && timeReportRows && (
        <MonthSummary
          formData={timeReportRows}
          monthWeeks={monthWeeks}
          allowanceSummary={allowanceSummary}
          compHours={compHours}
        ></MonthSummary>
      )}
      {(isSalaryAdmin || loggedInUserId === employeeId) && (
        <StyledButton size="sm" onClick={onCreateAllowanceClicked}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.CreateAllowance)}
        </StyledButton>
      )}
    </>
  );
};

export default forwardRef(CompFormContainer);
